import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  IKlearlyAdmin,
  EmployeeType,
} from "../../interfaces/klearlyAdmin.interface";
import {
  createKlearlyAdminServer,
  deleteKlearlyAdminServer,
  editKlearlyAdminServer,
  getKlearlyAdminsFromServer,
} from "./adminsApi";

export interface AdminsState {
  admins: IKlearlyAdmin[];
  loading: boolean;
  error?: string;
  filter?: {
    employeeType?: EmployeeType;
    search?: string;
  };
}

const initialState: AdminsState = {
  admins: [],
  loading: false,
  error: undefined,
  filter: {},
};

export const fetchAdmins = createAsyncThunk<
  IKlearlyAdmin[],
  { employeeType?: EmployeeType; search?: string } | undefined
>("admins/fetchAdmins", async (params) => {
  const res = await getKlearlyAdminsFromServer(params);
  return res.admins || [];
});

export const createAdmin = createAsyncThunk<
  { admin: IKlearlyAdmin },
  Partial<IKlearlyAdmin>
>("admins/createAdmin", async (admin) => {
  const res = await createKlearlyAdminServer(admin);
  return res;
});

export const editAdmin = createAsyncThunk<
  IKlearlyAdmin,
  Partial<IKlearlyAdmin>
>("admins/editAdmin", async (admin) => {
  const res = await editKlearlyAdminServer(admin);
  return res;
});

export const deleteAdmin = createAsyncThunk<string, string>(
  "admins/deleteAdmin",
  async (email) => {
    await deleteKlearlyAdminServer(email);
    return email;
  }
);

const adminsSlice = createSlice({
  name: "admins",
  initialState,
  reducers: {
    setEmployeeTypeFilter: (
      state,
      action: PayloadAction<EmployeeType | undefined>
    ) => {
      state.filter = { ...state.filter, employeeType: action.payload };
    },
    setSearchFilter: (state, action: PayloadAction<string | undefined>) => {
      state.filter = { ...state.filter, search: action.payload };
    },
    resetAdminsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.admins.unshift(action.payload.admin);
      })
      .addCase(editAdmin.fulfilled, (state, action) => {
        const idx = state.admins.findIndex((a) => a.id === action.payload.id);
        if (idx >= 0) state.admins[idx] = action.payload;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        const email = action.payload.toLowerCase();
        state.admins = state.admins.filter(
          (a) => a.email?.toLowerCase() !== email
        );
      });
  },
});

export const { setEmployeeTypeFilter, setSearchFilter, resetAdminsState } =
  adminsSlice.actions;

export const selectAdmins = (state: RootState) => state.admins.admins;
export const selectAdminsLoading = (state: RootState) => state.admins.loading;
export const selectAdminsFilter = (state: RootState) => state.admins.filter;

export default adminsSlice.reducer;
